import {
  Component,
  ElementRef,
  viewChild,
  input,
  inject,
  computed,
  ResourceRef,
  effect,
  untracked,
  signal,
  DestroyRef,
  Pipe,
  PipeTransform,
} from "@angular/core";
import * as d3 from "d3";
import { JobStateService } from "../job-state.store";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { ViewerSettingsService } from "../viewer-settings.store";
import { BehaviorSubject, debounceTime, distinctUntilChanged } from "rxjs";
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from "@angular/core/rxjs-interop";
import { SonicDataResourceRes } from "../inline-slice/inline-slice.component";
import { HorizonWithData } from "../../../../models/generalizedHorizon";

interface DepthHorizonData {
  intersection_points: {
    x1: number;
    x2: number;
    depth: number;
    distance: number;
  }[];
  target_depth: number;
  tolerance: number;
}

@Component({
  selector: "app-my-viewer",
  standalone: false,
  templateUrl: "./my-viewer.component.html",
  styleUrl: "./my-viewer.component.less",
})
export class MyViewerComponent {
  private destroyRef = inject(DestroyRef);

  // Generate a unique ID for this specific component instance
  public clipId = `chart-area-clip-${Math.random().toString(36).substring(2, 9)}`;

  public mouseCoords = signal<{ x: number; y: number } | null>(null);

  protected jobState = inject(JobStateService);
  protected viewerSettings = inject(ViewerSettingsService);
  private notificationService = inject(NzNotificationService);

  private currentJobId = computed(() => this.jobState.jobData().id);
  private lastJobIdForRenders: string | null = null;
  private staleImageForJobChange: string | null = null;

  // Inputs
  sliceImage = input.required<string>();
  sliceArray = input.required<number[][]>();
  colorbar = input.required<string>();
  limits = input.required<{ min: number; max: number }>();
  xIndicator = input.required<number>();
  yIndicator = input.required<number>();
  xLabel = input.required<string>();
  yLabel = input.required<string>();
  horizonDataResource = input<ResourceRef<HorizonWithData[]>>();
  sonicDataResource = input<ResourceRef<any>>();
  showSonicLocations = input<boolean>(false); // this is specifically for the depthSlice pretty much, cause in the depth slice we don't really show the sonics LOGS, but just their positions
  shotsOverlay = input<any[]>();
  viewerHeight = input<number>(0);
  flippedYAxis = input<boolean>(true);
  isDepth = input<boolean>(false);
  xCrop = input<number[] | null>(null);
  yCrop = input<number[] | null>(null);

  private container =
    viewChild.required<ElementRef<HTMLDivElement>>("container");
  private resizeObserver!: ResizeObserver;
  private containerWidth$ = new BehaviorSubject<number>(0);
  private containerWidth = toSignal(
    this.containerWidth$.pipe(takeUntilDestroyed(), debounceTime(500)),
    { initialValue: 0 },
  );

  // aliases
  dx = computed(() => this.jobState.jobData().modelGrid.dx);

  // Margins to make room for axes
  protected margin = { top: 5, right: 10, bottom: 33, left: 50 };

  // D3 Selections
  private svgSelection!: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private colorbarGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private metricsGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private chartGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private mainImage!: d3.Selection<SVGImageElement, unknown, null, undefined>;
  private horizonGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private sonicGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private shotOverlayGroup!: d3.Selection<
    SVGGElement,
    unknown,
    null,
    undefined
  >;
  private zoomableContentGroup!: d3.Selection<
    SVGGElement,
    unknown,
    null,
    undefined
  >;
  private hLine!: d3.Selection<SVGLineElement, unknown, null, undefined>;
  private vLine!: d3.Selection<SVGLineElement, unknown, null, undefined>;

  private xAxisGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private yAxisGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;

  // max - min [range] - color scale for shots. used only in depthSlice
  // the domain is possibly reveresed i think. tho pretty inconsequential
  // its mainly to reverse the color direction
  private colorScale = d3
    .scaleSequential()
    .domain([100, 0])
    .interpolator(d3.interpolateGreys);

  // D3 Scales & Behaviors
  private xScale = d3.scaleLinear();
  private yScale = d3.scaleLinear();
  private zoomBehavior = d3.zoom<SVGSVGElement, unknown>();

  colorbarWidth: number = 200;
  colorbarHeight: number = 20;
  xText!: string;
  yText!: string;
  valueText: any;

  // 1. The "Raw" value (updates instantly)
  private rawFocusedShot = signal<any | null>(null);
  // 2. The "UI" value (updates after a delay)
  // We use toObservable and debounceTime to filter the noise
  protected focusedShot = toSignal(
    toObservable(this.rawFocusedShot).pipe(
      debounceTime(50), // Wait 50ms of stillness before updating the UI
      distinctUntilChanged(), // Only update if it's actually a different shot
    ),
  );

  private sonic_scaleX!: d3.ScaleLinear<number, number, never>;
  private sonic_scaleY!: d3.ScaleLinear<number, number, never>;

  // Dimensions of the modelGrid as of the last updateImage() call
  private lastModelGridForRenders: {
    nx1: number;
    nx2: number;
    nx3: number;
    dx: number;
  } | null = null;
  // Whether the dimensions of the modelGrid changed when the job changed
  private dimensionsChangedForJob = false;

  constructor() {
    effect(() => this.updateDimensions());
    effect(() => this.updateImage());
    effect(() => this.makeHorizon());
    effect(() => this.makeSonic());
    effect(() => this.indicatorLines());
    effect(() => this.markSonicLocationsOnDepthSlice());
    effect(() => this.updateSonicMarkerColors());
    effect(() => this.onMouseCordsChanged());
    effect(() => this.makeShotOverlay());
    effect(() => this.changeDepthShotRadius());
    effect(() => this.onShotOverlayMetricChanged());
    effect(() => this.makeSonicMakers());
    effect(() => this.axisUnitsChanged());
    effect(() => this.toggleMarkerVisibility());
  }

  private axisUnitsChanged() {
    const units = this.viewerSettings.axisUnits();

    // strop if not initialized
    if (!this.svgSelection) return;

    // renderAxes already takes into account units inside it
    this.renderAxes(this.xScale, this.yScale);
    // also changed the x and y axis label units
    this.yAxisGroup.select("text").text(`${this.yLabel()} (${units})`);
    this.xAxisGroup.select("text").text(`${this.xLabel()} (${units})`);
  }

  private changeDepthShotRadius() {
    if (this.isDepth() && this.viewerSettings.showShotOverlay()) {
      let shotRadius = this.viewerSettings.depthShotRadius();

      this.shotOverlayGroup.selectAll(".shot-circle").attr("r", shotRadius);
    }
  }

  private makeShotOverlay(): void {
    if (!this.isDepth()) return;
    // given the scenario, this will only successfully go through if depth slice, since only in that scenario fo we have a shotsOverlayResource
    if (
      !this.viewerSettings.showShotOverlay() ||
      !this.shotsOverlay() ||
      this.shotsOverlay().length == 0
    ) {
      this.shotOverlayGroup.selectAll("*").remove(); // remove all things inside
      return;
    }

    let shotRadius = untracked(() => this.viewerSettings.depthShotRadius());
    let selectedMetric = untracked(() =>
      this.viewerSettings.selectedShotOverlayMetric(),
    );

    let shotsOverlay = this.shotsOverlay();
    const shots = this.shotOverlayGroup
      .selectAll(".shot-circle")
      .data(shotsOverlay, (d: any) => d.shotNo);

    this.shotOverlayGroup
      .on("mouseover", (event) => {
        const target = event.target as SVGCircleElement;
        if (!target.classList.contains("shot-circle")) return;

        const selection = d3.select(target);
        selection.interrupt(); // Interrupt any pending animations on circle
        selection
          .raise()
          .transition()
          .duration(100)
          .attr("r", this.viewerSettings.depthShotRadius() + 2);
        this.rawFocusedShot.set(selection.datum() as any);
      })
      .on("mouseout", (event) => {
        this.rawFocusedShot.set(null);
        const target = event.target as SVGCircleElement;
        if (!target.classList.contains("shot-circle")) return;

        const selection = d3.select(target);
        selection.interrupt();
        selection
          .transition()
          .duration(100)
          .attr("r", this.viewerSettings.depthShotRadius());
      })
      .on("mouseleave", () => {
        this.rawFocusedShot.set(null);
        this.shotOverlayGroup
          .selectAll(".shot-circle")
          .interrupt()
          .transition()
          .duration(100)
          .attr("r", this.viewerSettings.depthShotRadius());
      });

    shots.join(
      (enter) =>
        enter
          .append("circle")
          .attr("class", "shot-circle")
          .attr("r", shotRadius)
          .attr("fill", (d) => this.colorScale(d[selectedMetric] || 0))
          .attr("cx", (d) => this.xScale(d.sx / this.dx()))
          .attr("cy", (d) => this.yScale(d.sy / this.dx())),
      (update) =>
        update
          .attr("cx", (d) => this.xScale(d.sx / this.dx()))
          .attr("cy", (d) => this.yScale(d.sy / this.dx()))
          .attr("fill", (d) => this.colorScale(d.trace_fit || 0)),
      (exit) => exit.remove(),
    );
  }

  private onShotOverlayMetricChanged() {
    if (this.isDepth() && this.viewerSettings.showShotOverlay()) {
      this.colorScale.domain([
        this.viewerSettings.shotColorMax(),
        this.viewerSettings.shotColorMin(),
      ]);
      this.shotOverlayGroup
        .selectAll(".shot-circle")
        .attr("fill", (d) =>
          this.colorScale(
            d[this.viewerSettings.selectedShotOverlayMetric()] || 0,
          ),
        );
    }
  }

  private onMouseCordsChanged(): void {
    const coords = this.mouseCoords();
    if (!coords) return;

    // Domain Values
    const dx = Math.floor(this.xScale.invert(coords.x));
    const dy = Math.floor(this.yScale.invert(coords.y));

    this.xText = (dx * this.dx()).toFixed(0);
    this.yText = (dy * this.dx()).toFixed(0);

    const data = this.sliceArray();
    const colIndex = dx;
    const rowIndex = this.flippedYAxis() ? dy : data.length - dy;
    if (data[rowIndex] && data[rowIndex][colIndex] !== undefined) {
      this.valueText = data[rowIndex][colIndex].toFixed(2);
    } else {
      this.valueText = "N/A";
    }
  }

  ngOnInit() {
    this.initializeGraph();
    this.destroyRef.onDestroy(() => {
      // Stop D3 timers or clear selections
      this.svgSelection.selectAll("*").remove();
    });
  }

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver((entries) => {
      this.containerWidth$.next(entries[0].contentRect.width);
    });
    this.resizeObserver.observe(this.container().nativeElement);
  }

  /**
   * Intializes the entire graph. Should theoretically only run when the component is initialized i.e. once for the entire life cycle
   */
  private initializeGraph() {
    // remove margin since this width can now be considered the 'internal width'
    let width =
      this.container().nativeElement.clientWidth -
      (this.margin.left + this.margin.right);
    let height = this.viewerHeight();
    if (width < 0) return;

    // the parent SVG take all the space available
    this.svgSelection = d3
      .select(this.container().nativeElement)
      .append("svg")
      .attr("width", width + this.margin.left + this.margin.right)
      .attr("height", height + this.margin.top + this.margin.bottom);

    // area for the colorbar legend
    this.colorbarGroup = this.svgSelection
      .append("g")
      .attr(
        "transform",
        `translate(${this.container().nativeElement.clientWidth - this.colorbarWidth - this.margin.right}, ${this.margin.top + height + this.margin.bottom - this.colorbarHeight})`,
      );

    // area for the co-ordinate + value displays i.e. Vp/density etc
    this.metricsGroup = this.svgSelection
      .append("g")
      .attr(
        "transform",
        `translate(${this.margin.left}, ${this.viewerHeight() + this.margin.top + this.margin.bottom})`,
      );
    this.metricsGroup
      .style("font-family", "Courier New, Courier, monospace")
      .append("text")
      .attr("font-size", "")
      .attr("class", "x-metric")
      .attr("y", 0);
    this.metricsGroup
      .style("font-family", "Courier New, Courier, monospace")
      .append("text")
      .attr("font-size", "")
      .attr("class", "y-metric")
      .attr("y", -10);
    this.metricsGroup
      .style("font-family", "Courier New, Courier, monospace")
      .append("text")
      .attr("font-size", "")
      .attr("class", "value-metric")
      .attr("y", -20);

    // defs for clip path [prevent image zooming from spilling over the axis]
    const defs = this.svgSelection.append("defs");
    defs
      .append("clipPath")
      .attr("id", this.clipId)
      .append("rect")
      .attr("height", height)
      .attr("width", width)
      .attr("x", 0)
      .attr("y", 0);

    // chart group will only take the 'available' space that it should have
    this.chartGroup = this.svgSelection
      .append("g")
      .attr("width", width)
      .attr("height", height)
      .attr("transform", `translate(${this.margin.left},${this.margin.top})`);

    // make the y and x axes groups and then renderAxes(). that will make the scales and apply
    this.xAxisGroup = this.chartGroup.append("g").attr("class", "x-axis-group");
    this.yAxisGroup = this.chartGroup.append("g").attr("class", "y-axis-group");

    this.yAxisGroup
      .append("text")
      .attr("class", "y-label")
      .attr(
        "transform",
        `
        translate(-35, ${height / 2})
        rotate(-90)
      `,
      )
      .attr("fill", "black")
      .attr("font-size", 12)
      .attr("font-weight", "bold")
      .style("text-anchor", "middle")
      .text(`${this.yLabel()} (${this.viewerSettings.axisUnits()})`);
    this.xAxisGroup
      .append("text")
      .attr("class", "x-label")
      .attr("transform", `translate(${width / 2}, 30)`)
      .attr("fill", "black")
      .attr("font-size", 12)
      .attr("font-weight", "bold")
      .style("text-anchor", "middle")
      .text(`${this.xLabel()} (${this.viewerSettings.axisUnits()})`);

    let xDomain = this.sliceArray()[0].length;
    let yDomain = this.sliceArray().length;
    this.xScale = d3.scaleLinear().domain([0, xDomain]).range([0, width]);
    // this mainly effects the depth slice. since in that, origin is 0,0 unlike inline and crossline slices
    if (this.flippedYAxis()) {
      this.yScale = d3.scaleLinear().domain([0, yDomain]).range([0, height]);
    } else {
      this.yScale = d3.scaleLinear().domain([yDomain, 0]).range([0, height]);
    }
    this.renderAxes(this.xScale, this.yScale);

    // add the image right about now
    // the image will be set in the imageUpdate function. all the effects will run after the ngOnInit
    this.mainImage = this.chartGroup
      .append("g")
      .attr("clip-path", `url(#${this.clipId})`)
      .append("image")
      .attr("width", width)
      .attr("height", height)
      .attr("class", "main-slice")
      .attr("preserveAspectRatio", "none");

    // mouse hover thingy on the mainImage
    this.mainImage.on("mousemove", (event) => {
      // 1. Get pointer position relative to the SVG container
      const [mx, my] = d3.pointer(event);
      // 2. Adjust for current zoom level to get "Original Image" coordinates
      const transform = d3.zoomTransform(this.mainImage.node());
      const realX = transform.invertX(mx);
      const realY = transform.invertY(my);
      // 3. Update the state (round to integers for clean display)
      this.mouseCoords.set({ x: realX, y: realY });
    });

    // make horizon group (will be leveraged to target making the horizon)
    this.zoomableContentGroup = this.chartGroup
      .append("g")
      .attr("class", "zoomable-content-container") // the container is where we apply the clipping
      .attr("clip-path", `url(#${this.clipId})`)
      .append("g")
      .attr("class", "zoomable-content"); // the internal G is the one on which we apply the transformations (zoom pan etc.). having clip path on this loses the point of the clipping
    this.horizonGroup = this.zoomableContentGroup
      .append("g")
      .attr("class", "horizonContainerGroup");
    this.sonicGroup = this.zoomableContentGroup
      .append("g")
      .attr("class", "sonicContainerGroup")
      .style("pointer-events", "none");
    this.zoomableContentGroup
      .append("g")
      .attr("class", "wellMarkersContainerGroup")
      .style("pointer-events", "none");

    const tooltipStyles = (
      sel: d3.Selection<HTMLDivElement, unknown, null, undefined>,
    ) =>
      sel
        .style("position", "absolute")
        .style("background", "rgba(0,0,0,0.75)")
        .style("color", "#fff")
        .style("padding", "3px 7px")
        .style("border-radius", "4px")
        .style("font-size", "12px")
        .style("pointer-events", "none")
        .style("white-space", "nowrap")
        .style("display", "none");

    tooltipStyles(
      d3
        .select(this.container().nativeElement)
        .append("div")
        .attr("class", "well-marker-tooltip"),
    );
    tooltipStyles(
      d3
        .select(this.container().nativeElement)
        .append("div")
        .attr("class", "horizon-tooltip"),
    );

    // Indicator line definitions [setup, later on we will only be updating]
    let indicatorGroup = this.zoomableContentGroup
      .append("g")
      .attr("class", "indicatorLines")
      .style("pointer-events", "none"); // Ensure lines don't interfere with mouse events
    this.hLine = indicatorGroup
      .append("line")
      .attr("class", "h-indicator")
      .attr("stroke", "white")
      .attr("stroke-width", 1)
      .style("stroke-dasharray", "5, 3")
      .attr("shape-rendering", "crispEdges");

    this.vLine = indicatorGroup
      .append("line")
      .attr("class", "v-indicator")
      .attr("stroke", "white")
      .attr("stroke-width", 1)
      .style("stroke-dasharray", "5, 3")
      .attr("shape-rendering", "crispEdges");

    // The following groups are specific to depth slices, hence conditional
    if (this.isDepth()) {
      this.shotOverlayGroup = this.zoomableContentGroup
        .append("g")
        .attr("class", "shotOverlayContainerGroup");
      // .data([null]) // Bind a single dummy data point
      // .join('g')    // Create if missing, keep if exists
    }

    // Set the zoom and Pan features: how much you can zoom, on which part, and what to do when there is a zoom
    this.zoomBehavior
      .scaleExtent([1, 20]) // Zoom from 1x to 20x
      .filter((event: any) => {
        // Always allow panning (mousedown/mousemove),
        // but only allow wheel zoom if Shift/Ctrl is pressed
        if (event.type === "wheel") {
          return event.shiftKey || event.ctrlKey || event.metaKey;
        }
        return true; // Allow all other mouse interactions
      })
      .on("zoom", (event) => this.handleZoom(event));
    this.zoomBehavior
      .translateExtent([
        [0, 0],
        [width, height],
      ])
      .extent([
        [0, 0],
        [width, height],
      ]);

    this.mainImage.call(this.zoomBehavior as any);
  }

  private cropImage(
    base64Str: string,
    startX: number,
    endX: number,
    startY: number,
    endY: number,
  ): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        let trueHeight = Number(this.mainImage.attr("height"));
        let trueWidth = Number(this.mainImage.attr("width"));

        let xDomain = this.sliceArray()[0].length;
        let yDomain = this.sliceArray().length;
        let unitHeight = Number(img.naturalHeight) / yDomain;
        let unitWidth = Number(img.naturalWidth) / xDomain;

        let startWidth = unitWidth * startX;
        let endWidth = unitWidth * endX;
        let cropWidth = endWidth - startWidth;
        // Y logic with flip handling
        let finalStartY: number;
        let finalEndY: number;
        if (this.flippedYAxis()) {
          finalStartY = startY;
          finalEndY = endY;
        } else {
          /**
           * If not flipped, startY is at the bottom.
           * The 'top' of the crop box in canvas-space is actually (yDomain - endY)
           */
          finalStartY = yDomain - endY;
          finalEndY = yDomain - startY;
        }
        let startHeight = unitHeight * finalStartY;
        let cropHeight = unitHeight * (finalEndY - finalStartY);

        if (ctx) {
          ctx.imageSmoothingEnabled = this.viewerSettings.smoothing();
          ctx.imageSmoothingQuality = "high";
          canvas.width = trueWidth;
          canvas.height = trueHeight;

          ctx.drawImage(
            img,
            startWidth,
            startHeight, // Source Start
            cropWidth,
            cropHeight, // Source Dimensions
            0,
            0, // Destination Start
            trueWidth,
            trueHeight, // Destination Dimensions (Scale to fit)
          );
        }

        resolve(canvas.toDataURL("image/png"));
      };
      img.src = base64Str;
    });
  }

  /**
   * Updates / Applies the image
   * Gets called:
   * 1. if sliceImage changes
   * 2. if smoothing changes
   */
  private async updateImage() {
    const jobId = this.currentJobId();
    const currentModelGrid = this.jobState.jobData().modelGrid;
    if (jobId !== this.lastJobIdForRenders) {
      // Job just changed: sliceImage() is still the previous job's image at this point
      this.lastJobIdForRenders = jobId;
      this.staleImageForJobChange = this.sliceImage();
      // Checking if the image dimensions match the new job dimensions - if they have changed, don't trust isCropped
      this.dimensionsChangedForJob =
        this.lastModelGridForRenders !== null &&
        (currentModelGrid.nx1 !== this.lastModelGridForRenders.nx1 ||
          currentModelGrid.nx2 !== this.lastModelGridForRenders.nx2 ||
          currentModelGrid.nx3 !== this.lastModelGridForRenders.nx3 ||
          currentModelGrid.dx !== this.lastModelGridForRenders.dx);
    }
    this.lastModelGridForRenders = currentModelGrid;
    // Check if the current image is the placeholder for the job change
    const isPlaceholderImageForJob = this.sliceImage() === this.staleImageForJobChange;

    // check if the ranges are fine.
    // i.e. if there is a need for cropping
    let smoothing = this.viewerSettings.smoothing();
    var newImage = null;
    if (this.xCrop() && this.yCrop()) {
      var [x1, x2] = this.xCrop()!;
      var [y1, y2] = this.yCrop()!;

      // divide all by dx to bring to nx units
      x1 /= this.dx();
      x2 /= this.dx();
      y1 /= this.dx();
      y2 /= this.dx();

      const xDomain = this.sliceArray()[0].length;
      const yDomain = this.sliceArray().length;
      const isCropped = x1 > 0 || y1 > 0 || x2 < xDomain || y2 < yDomain;
      
      const cropImageCheck = () => {
        if (isPlaceholderImageForJob && this.dimensionsChangedForJob) {
          return false;
        }
        return isCropped;
      }
      if (cropImageCheck()) {
        // if x1, x2 and y1, y2 are equal to the true width, prolly don't need to update the data then
        newImage = await this.cropImage(this.sliceImage(), x1, x2, y1, y2);
      } else {
        newImage = this.sliceImage();
      }
      // change the domain of the y and x axis to accomodate for the changes in cropping and plot them
      this.xScale.domain([x1, x2]);
      if (this.flippedYAxis()) this.yScale.domain([y1, y2]);
      else this.yScale.domain([y2, y1]);
      this.renderAxes(this.xScale, this.yScale);
    } else {
      newImage = this.sliceImage();
    }
    this.mainImage.attr("href", newImage).attr("imageSmoothingEnabled");
    if (smoothing) this.mainImage.attr("image-rendering", "auto");
    else this.mainImage.attr("image-rendering", "pixelated");

    untracked(() => {
      this.makeSonic(); // This updates the sonic log
      this.makeSonicMakers(); // updates sonic markers
      this.makeHorizon(); // This updates the horizon
      this.markSonicLocationsOnDepthSlice(); // this updates the sonic locations on depthSlice
      this.makeShotOverlay();
    });
  }

  /**
   * Function for handling zoom from d3 graph
   * @param event d3 native Zoom event
   */
  private handleZoom(event: d3.D3ZoomEvent<SVGSVGElement, any>) {
    const transform = event.transform;
    const { k } = event.transform;
    // Rescale the scales based on the zoom transform
    const newX = transform.rescaleX(this.xScale);
    const newY = transform.rescaleY(this.yScale);

    // Update the Axes
    this.renderAxes(newX, newY);

    // Update the Image transformation
    // We transform the image directly for the smoothest performance
    this.mainImage.attr("transform", transform.toString());
    this.zoomableContentGroup
      // .selectChildren("g")
      .attr("transform", transform.toString());

    // counter scale the text size for the well log markers (inline and crossline)
    this.svgSelection
      .selectAll(".marker-labels")
      .style("font-size", `${8 / k}px`);

    // counter scale for the well markers (symbols on the depth slice)
    this.svgSelection?.selectAll(".well-marker").attr("transform", (d: any) => {
      // original pixel position
      const x = this.xScale(d["crossline"]);
      const y = this.yScale(d["inline"]);

      // translate to position and then scale down by k
      return `translate(${x}, ${y}) scale(${1 / k})`;
    });
  }

  // reset zoom
  public resetZoom() {
    this.mainImage
      .transition()
      .duration(300)
      .call(this.zoomBehavior.transform as any, d3.zoomIdentity);
  }

  private ticksResolution(
    values: number[],
    dx: number,
    units: "km" | "kft" = "km",
  ) {
    // we do the min max cause the 'ranges' can be reversed hence the values could be in either order
    let startValue = Math.min(...values);
    let endValue = Math.max(...values);
    let unit_dx = dx * (units == "km" ? 1 : 3.28084); // the dx will be in kft or km depending on the users selected choice
    let applicableRange = (endValue - startValue) * unit_dx;

    let options = [100, 200, 500, 1000, 2000, 2500, 5000, 10000]; // options for intervals
    let i = 0;

    // Choose the best interval option
    while (i < options.length - 1 && applicableRange / options[i] >= 15) {
      i++;
    }
    let selectedInterval = options[i];
    let countTicks = Math.floor(applicableRange / selectedInterval);
    let dxInterval = selectedInterval / unit_dx;

    // Initialize ticks array. has zero for the origin tick marker
    let ticks = [0];
    for (let j = 1; j <= countTicks; j++) {
      ticks.push(startValue + j * dxInterval);
    }
    return ticks;
  }

  /**
   * Gets the scales and then makes them on the graph. also manages the y transform for the x-axis
   * @param xScale d3 ScaleLinear object
   * @param yScale d3 ScaleLinear object
   */
  private renderAxes(
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>,
  ) {
    let unit = untracked(() => this.viewerSettings.axisUnits());

    this.xAxisGroup
      .call(
        d3
          .axisBottom(xScale)
          .tickValues(this.ticksResolution(xScale.domain(), this.dx(), unit))
          .tickFormat((d) =>
            (Number(d) * this.dx() * 0.001 * (unit == "km" ? 1 : 3.28084))
              .toFixed(1)
              .toString(),
          ),
      )
      .attr("transform", `translate(0,${this.viewerHeight()})`);
    this.yAxisGroup.call(
      d3
        .axisLeft(yScale)
        .tickValues(this.ticksResolution(yScale.domain(), this.dx(), unit))
        .tickFormat((d) =>
          (Number(d) * this.dx() * 0.001 * (unit == "km" ? 1 : 3.28084))
            .toFixed(1)
            .toString(),
        ),
    );
    this.indicatorLines();
  }

  /**
   * Makes each horizon line given the data.
   * Gets called:
   * 1. if the horizon data changes
   * 2. initializing graph
   * 3. updateing dimension
   */
  private makeHorizon() {
    if (!this.horizonGroup || !this.horizonDataResource()) return;

    if (this.horizonDataResource().error()) {
      this.notificationService.error(
        "Horizon data error",
        `${this.horizonDataResource().error()}`,
      );
      return;
    }

    this.horizonGroup.selectAll("*").remove();
    if (this.isDepth()) {
      let data = this.horizonDataResource().value();
      if (!data) return
      const colorMap = new Map(
        this.jobState.selectedHorizon().map((h) => [h.id, h.color]),
      );
      this.horizonGroup
        .selectAll(".depth-horizon-container")
        .data(data, (d: any) => d.id)
        .join("g") // handle s updates and creation andall
        .attr("class", "depth-horizon-container") 
        .attr('fill', (h: any) => colorMap.get(h.id) ?? h.color) // inherited by the circles inside
        .selectAll(".depth-horizon-marker") // run inner for the plots in the depth horizons
        .data((h: any) => h.data.intersection_points ?? [])
        .join("circle")
        .attr("class", "depth-horizon-marker")
        .attr("cx", (d: any) => this.xScale(d.x1))
        .attr("cy", (d: any) => this.yScale(d.x2))
        .attr("r", 1); 
    } else {
      let data = this.horizonDataResource().value();
      if (!data || data.length == 0) return;
      // remove everything that is currently in this group.
      var line = d3
        .line<number>()
        .defined((d) => d >= 0) //remove all -1, so just skip those
        .x((_, index) => this.xScale(index)) //relative to the xAxis
        .y((d) => this.yScale(d));

      const colorMap = new Map(
        this.jobState.selectedHorizon().map((h) => [h.id, h.color]),
      );
      const tooltip = d3
        .select(this.container().nativeElement)
        .select<HTMLDivElement>(".horizon-tooltip");

      data.forEach((horizon) => {
        const pathD = line(horizon.data);
        const color = colorMap.get(horizon.id) ?? horizon.color;

        // visible stroke
        const visiblePath = this.horizonGroup
          .append("path")
          .attr("d", pathD)
          .attr("stroke-width", "2px")
          .attr("stroke", color)
          .attr("fill", "none")
          .style("pointer-events", "none")
          .style("transition", "stroke-width 0.15s ease, filter 0.15s ease");

        // wider invisible hit area
        this.horizonGroup
          .append("path")
          .attr("d", pathD)
          .attr("stroke-width", "10px")
          .attr("stroke", "transparent")
          .attr("fill", "none")
          .style("pointer-events", "stroke")
          .style("cursor", "crosshair")
          .on("mouseover", (event: MouseEvent) => {
            visiblePath
              .attr("stroke-width", "2px")
              .style("filter", `drop-shadow(0 0 4px #666)`);
            const rect = (
              tooltip.node()!.parentElement as HTMLElement
            ).getBoundingClientRect();
            tooltip
              .style("display", "block")
              .style("left", `${event.clientX - rect.left + 10}px`)
              .style("top", `${event.clientY - rect.top - 24}px`)
              .style("border-left", `3px solid ${color}`)
              .text(horizon.name);
          })
          .on("mousemove", (event: MouseEvent) => {
            const rect = (
              tooltip.node()!.parentElement as HTMLElement
            ).getBoundingClientRect();
            tooltip
              .style("left", `${event.clientX - rect.left + 10}px`)
              .style("top", `${event.clientY - rect.top - 24}px`);
          })
          .on("wheel", (event: WheelEvent) => event.preventDefault(), {
            passive: false,
          } as any)
          .on("mouseout", () => {
            visiblePath.attr("stroke-width", "2px").style("filter", "none");
            tooltip.style("display", "none");
          });
      });
    }
  }

  private toggleMarkerVisibility() {
    let markersVisibility = this.jobState.markersVisibility();
    if (
      !this.sonicGroup ||
      this.sonicDataResource()?.isLoading() ||
      this.sonicDataResource()?.error()
    )
      return false;
    Object.keys(markersVisibility).map((k) => {
      let markerG = this.sonicGroup.select(
        `g.marker-${k.replace(/[^a-z0-9-_]/gi, "-")}`,
      );
      markerG.attr("visibility", markersVisibility[k] ? "visible" : "hidden");
    });
  }

  private makeSonicMakers() {
    // empty the group if it previously had something
    this.sonicGroup.selectAll("g.sonic-markers-groups").remove();
    let showWellMarkers = this.viewerSettings.showWellMarkers();
    var sonicData: SonicDataResourceRes;
    try {
      sonicData = this.sonicDataResource().value();
    } catch {
      if (this.sonicDataResource()?.error()) {
        console.error(
          "Cannont make sonic markers",
          this.sonicDataResource().error(),
        );
      }
    }
    if (
      !this.sonicGroup ||
      !this.sonicDataResource() ||
      !showWellMarkers ||
      !sonicData
    ) {
      return;
    }

    let { data, x, markers } = sonicData;
    let startDepth = data[0].depth;
    let xWidth = 50;

    const markersG = this.sonicGroup
      .append("g")
      .attr("class", "sonic-markers-groups")
      .attr(
        "transform",
        "translate(" +
          this.xScale(x) +
          "," +
          this.yScale(startDepth / this.dx()) +
          ")",
      );
    if (markers?.length > 0) {
      let innerContainer = markersG
        .selectAll(".sonic-marker")
        .data(markers)
        .join("g")
        .attr(
          "class",
          (d) => `depth-marker marker-${d.m.replace(/[^a-z0-9-_]/gi, "-")}`,
        )
        .attr(
          "transform",
          (d: { z: number; m: string }) =>
            `translate(0, ${this.sonic_scaleY(d.z)})`,
        );
      innerContainer
        .append("line")
        .attr("x1", 0)
        .attr("x2", xWidth + 20)
        .attr("stroke", "black") // Or use a color based on the name
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "4,2")
        .style("vector-effect", "non-scaling-stroke");
      innerContainer
        .append("text")
        .attr("class", "marker-labels")
        .attr("x", xWidth + 20)
        // .attr('y', 2) // Offset slightly above the line
        .text((d: { z: number; m: string }) => d.m)
        .attr("fill", "black")
        .style("font-size", "8px");
    }
  }

  /**
   * Makes sonic log given the data.
   * Gets called:
   * 1. if the sonic data changes
   * 2. initializing graph
   * 3. updateing dimension
   */
  private makeSonic() {
    // remove all inside first. including defs. we don't do a selectAll cause it also has the sonicMarker
    this.sonicGroup?.selectAll(".sonic-graph").remove();
    this.sonicGroup?.selectAll("defs").remove();

    if (!this.sonicGroup || !this.sonicDataResource()) return;

    if (this.sonicDataResource().error()) {
      this.notificationService.error(
        "Sonic Log error",
        `${this.sonicDataResource().error()}`,
      );
      return;
    }

    let sonicData = this.sonicDataResource().value();
    if (!sonicData) return;

    let { data, x } = sonicData;
    // y is the depth
    // x is the velocity
    let startDepth = data[0].depth;
    let endDepth = data[data.length - 1].depth;
    let startDepth_dx = Math.floor(startDepth / this.dx());
    const maxNx3 = untracked(() => this.jobState.jobData().modelGrid.nx3);
    let endDepth_dx = Math.min(Math.ceil(endDepth / this.dx()), maxNx3 - 1);

    // start from the starting startDX, and go till endDX
    let correspondingVelocityData = [];
    const sliceArray = this.sliceArray();
    for (let i = startDepth_dx; i <= endDepth_dx; i++) {
      const depth = i * this.dx();
      const velocity = sliceArray[i][x];
      correspondingVelocityData.push({
        depth: depth,
        velocity: velocity,
      });
    }
    let yDomain = [startDepth, endDepth];
    let yHeight =
      this.yScale(endDepth / this.dx()) - this.yScale(startDepth / this.dx());
    let yRange = [0, yHeight];

    let xDomain = [this.limits().min, this.limits().max];
    // let xDomain = [
    //   Math.min(...data.map((d) => d.velocity)),
    //   Math.max(...data.map((d) => d.velocity)),
    // ];
    let xWidth = 50;

    // Vp plus DenChange, add perturb, VP, startVP, impedance

    this.sonic_scaleX = d3.scaleLinear().domain(xDomain).range([0, xWidth]);
    this.sonic_scaleY = d3.scaleLinear().domain(yDomain).range(yRange);

    this.sonicGroup
      .append("defs")
      .append("clipPath")
      .attr("id", "sonic-log-clip")
      .append("rect")
      .attr("width", xWidth + 20)
      .attr("height", yHeight);

    let contentG = this.sonicGroup
      .append("g")
      .attr("class", "sonic-graph")
      .attr("clip-path", "url(#sonic-log-clip)")
      .attr(
        "transform",
        "translate(" +
          this.xScale(x) +
          "," +
          this.yScale(startDepth / this.dx()) +
          ")",
      );

    // if sonic is not to be shown, i.e. show only the markers, hide the sonic by returning early after the scales have been defined
    if (!this.viewerSettings.showSonicLog()) return;

    contentG
      .append("rect")
      .attr("width", xWidth + 20)
      .attr("height", yHeight)
      .attr("fill", "white")
      .attr("opacity", "0.5");

    const GAP_THRESHOLD = 100; // If depth jumps by more than 100 units, break the line
    let sonicLine = d3
      .line()
      .x((d: any) => {
        return this.sonic_scaleX(d.velocity);
      })
      .y((d: any) => {
        return this.sonic_scaleY(d.depth);
      })
      .curve(d3.curveMonotoneY);

    const showSonicSamples = this.viewerSettings.showSonicSamples();
    if (!showSonicSamples) {
      sonicLine.defined((d: any, i, arr) => {
        if (i === 0) return true;
        const prev: any = arr[i - 1];
        return d.depth - prev.depth < GAP_THRESHOLD;
      });
    }

    // showSonicSamples=false → gapped line, no dots
    // showSonicSamples=true  → continuous line + dots showing every sample
    contentG
      .append("path")
      .attr("width", xWidth)
      .attr("height", yHeight)
      .attr("transform", "translate(10, 0)")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", "steelblue")
      .attr("stroke-width", 0.5)
      .attr("d", sonicLine);

    if (showSonicSamples) {
      contentG
        .append("g")
        .attr("transform", "translate(10, 0)")
        .selectAll("circle")
        .data(data)
        .join("circle")
        .attr("cx", (d: any) => this.sonic_scaleX(d.velocity))
        .attr("cy", (d: any) => this.sonic_scaleY(d.depth))
        .attr("r", 1.5)
        .attr("fill", "steelblue");
    }

    let currentModel = untracked(() => this.jobState.selectedModel());
    // TODO: temporary hack to not show model profile for RTMs. do a proper fix in the future
    if (currentModel !== "RTM") {
      contentG
        .append("path")
        .attr("width", xWidth)
        .attr("height", yHeight)
        .attr("transform", "translate(10, 0)")
        .datum(correspondingVelocityData)
        .attr("fill", "none")
        .attr("stroke", "red")
        .attr("stroke-width", 0.5)
        .attr("d", sonicLine);
    }
  }

  /**
   * Makes each indicator line
   * Gets called:
   * 1. if the x/y lines change
   * 2. initializing graph
   * 3. updateing dimension
   * 4. this.viewerSettings.gridLines() changes
   */
  private indicatorLines() {
    if (!this.hLine && !this.vLine) return;

    if (!this.viewerSettings.gridLines()) {
      this.hLine.attr("x1", 0).attr("x2", 0).attr("y1", 0).attr("y2", 0);
      this.vLine.attr("x1", 0).attr("x2", 0).attr("y1", 0).attr("y2", 0);
      return;
    }
    const yPos = this.yScale(this.yIndicator());
    const xPos = this.xScale(this.xIndicator());
    const [xMin, xMax] = this.xScale.range();
    const [yMin, yMax] = this.yScale.range();

    const indicatorColor = this.jobState
      .selectedCmap()
      .includes("red_white_blue")
      ? "black"
      : "white";
    this.hLine.attr("stroke", indicatorColor);
    this.vLine.attr("stroke", indicatorColor);

    // Direct reference update - no searching, no diffing
    this.hLine
      .attr("x1", xMin)
      .attr("x2", xMax)
      .attr("y1", yPos)
      .attr("y2", yPos);

    this.vLine
      .attr("x1", xPos)
      .attr("x2", xPos)
      .attr("y1", yMin)
      .attr("y2", yMax);
  }

  /**
   * Updates the dimensions to match the new available ones for the entire graph.
   * Gets called:
   * 1. width or height changes.
   * 2. first render
   */
  private updateDimensions() {
    // new internal dimensions
    const w = this.containerWidth() - (this.margin.left + this.margin.right);
    const h = this.viewerHeight();
    if (w <= 0 || h <= 0) return;

    // reusable transition for all
    const t = this.svgSelection
      .transition()
      .duration(500) as unknown as d3.Transition<d3.BaseType, any, any, any>;

    // update position of legend
    this.colorbarGroup
      .transition(t)
      .attr(
        "transform",
        `translate(${this.containerWidth() - this.colorbarWidth - this.margin.right}, ${this.margin.top + h + this.margin.bottom - this.colorbarHeight})`,
      );

    // update xLabel and yLabel positions
    this.xAxisGroup
      .select(".x-label")
      .transition(t)
      .attr("transform", `translate(${w / 2}, 30)`);
    this.yAxisGroup
      .select(".y-label")
      .transition(t)
      .attr("transform", `translate(-35, ${h / 2}) rotate(-90)`);

    // update the parent SVG size. this is with the margins ofcourse
    this.svgSelection
      .transition(t)
      .attr("width", w + this.margin.left + this.margin.right)
      .attr("height", h + this.margin.top + this.margin.bottom);

    // update the Clip Path rect so the image doesn't bleed out
    this.svgSelection
      .transition(t)
      .select(`#${this.clipId} rect`)
      .attr("width", w)
      .attr("height", h);

    // update scales
    this.xScale.range([0, w]);
    this.yScale.range([0, h]);

    // update the background reference rect
    this.chartGroup
      .select("rect")
      .transition(t)
      .attr("width", w)
      .attr("height", h);

    // Update the main image
    this.mainImage.transition(t).attr("width", w).attr("height", h);

    // update the shots for depth slice
    untracked(() => {
      if (this.isDepth() && this.viewerSettings.showShotOverlay()) {
        this.shotOverlayGroup
          .selectAll(".shot-circle")
          .transition(t)
          .attr("cx", (d: any) => this.xScale(d["sx"] / this.dx())) // they have their values still. the scales have been updated so simply recaclulate
          .attr("cy", (d: any) => this.yScale(d["sy"] / this.dx()));
      }
    });

    // update for the well-markers in the depth slice. simply won't do anything otherwise
    this.zoomableContentGroup
      .selectAll(".well-marker")
      .transition(t)
      .attr("transform", (d: any) => {
        // Re-map the existing data to the new scale values
        const x = this.xScale(d.crossline);
        const y = this.yScale(d.inline);
        return `translate(${x}, ${y})`;
      });

    // re-apply Zoom Extents, zoom needs to know the new bounds
    this.zoomBehavior
      .translateExtent([
        [0, 0],
        [w, h],
      ])
      .extent([
        [0, 0],
        [w, h],
      ]);

    // all of the below functions may have additional signals. but they should not be a dependency for the effect running this function
    // as such, wrapping them in untracked ensures that the signals in these will not be tracked for dependency of this effect
    untracked(() => {
      this.renderAxes(this.xScale, this.yScale); // Redraw the Axes and Indicators
      this.makeSonic(); // This updates the sonic log
      this.makeSonicMakers(); // updates sonic markers
      this.makeHorizon(); // This updates the horizon
    });
  }

  /**
   * This function is specifically only for the depth slice. we can just set it as true and false, but its to show the sonic locations in the depth slice
   * in the depth slice, the sonics are not shown as logs but rathers marks simply to show their locations.
   */
  private markSonicLocationsOnDepthSlice() {
    const container = this.zoomableContentGroup.select(
      ".wellMarkersContainerGroup",
    );

    if (!this.showSonicLocations()) {
      container.selectAll("*").remove();
      return;
    }

    const wells = this.jobState.jobData().wellLogs;
    const symbol = d3.symbol().type(d3.symbolCross).size(100);

    const tooltip = d3
      .select(this.container().nativeElement)
      .select<HTMLDivElement>(".well-marker-tooltip");

    container
      .selectAll(".well-marker")
      .data(wells, (d: any) => d.id)
      .join(
        (enter) => enter.append("path").attr("class", "well-marker"),
        (update) => update,
        (exit) => exit.remove(),
      )
      .attr("d", symbol)
      .attr("fill", "gold")
      .attr("stroke", "black")
      .style("pointer-events", "all")
      .style("cursor", "pointer")
      .attr("transform", (d) => {
        const x = this.xScale(d.crossline);
        const y = this.yScale(d.inline);
        return `translate(${x}, ${y})`;
      })
      .on("mouseover", function (event: MouseEvent, d: any) {
        const rect = (
          tooltip.node()!.parentElement as HTMLElement
        ).getBoundingClientRect();
        tooltip
          .style("display", "block")
          .style("left", `${event.clientX - rect.left + 10}px`)
          .style("top", `${event.clientY - rect.top - 24}px`)
          .text(d.name);
      })
      .on("mousemove", function (event: MouseEvent) {
        const rect = (
          tooltip.node()!.parentElement as HTMLElement
        ).getBoundingClientRect();
        tooltip
          .style("left", `${event.clientX - rect.left + 10}px`)
          .style("top", `${event.clientY - rect.top - 24}px`);
      })
      .on("mouseout", function () {
        tooltip.style("display", "none");
      })
      .on("click", (_event: MouseEvent, d: any) => {
        this.jobState.selectedLog.set(d);
      });

    container.raise();
    untracked(() => this.updateSonicMarkerColors());
  }

  private updateSonicMarkerColors() {
    const selectedLogId = this.jobState.selectedLog()?.id;
    const container = this.zoomableContentGroup?.select(
      ".wellMarkersContainerGroup",
    );
    if (!container) return;

    container
      .selectAll<SVGPathElement, any>(".well-marker")
      .attr("fill", (d) => (d.id === selectedLogId ? "green" : "gold"))
      .each(function (d) {
        if (d.id === selectedLogId) d3.select(this).raise();
      });
  }

  ngOnDestroy() {
    // Crucial: Disconnect to prevent memory leaks
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}

@Pipe({ name: "significantFigures" })
export class SignificantFiguresPipe implements PipeTransform {
  transform(value: number, digits: number): string {
    if (value == null) return "";
    return value.toPrecision(digits);
  }
}
